"use client";

import { useEffect, useState } from "react";

// Per Next.js App Router, `error.tsx` is a client component. It receives an
// error with an optional `digest` — that digest is the same id kudu-api
// echoes as `extensions.requestId` when the failure originated server-side.
// We surface it with a Copy button so support can grep both ends of the
// wire from a screenshot.
export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    // Sentry wiring lands in chunk 47. Until then the error surfaces in
    // the framework's server log; the in-page reference id is what users
    // share with support.
  }, []);

  const reference = error.digest ?? "—";

  async function copy() {
    if (!error.digest) return;
    try {
      await navigator.clipboard.writeText(error.digest);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard can fail under permission lockdowns; do nothing.
    }
  }

  return (
    <main className="min-h-[60vh] flex items-center justify-center px-8 py-32">
      <div className="text-center max-w-md">
        <p className="eyebrow-accent mb-6">Something went wrong</p>
        <h1 className="font-display text-[var(--text-section)] leading-[1.05] mb-6">
          We hit an unexpected error.
        </h1>
        <p className="text-muted text-[var(--text-body)] leading-[1.7] mb-8">
          Our team has been notified. You can try again, or head back to the home page.
        </p>
        <p className="text-muted text-[11px] tracking-[1.5px] uppercase mb-10 inline-flex items-center gap-2">
          <span>Reference: </span>
          <code className="font-mono text-[12px] normal-case tracking-normal">{reference}</code>
          {error.digest ? (
            <button
              type="button"
              onClick={copy}
              className="text-[10.5px] underline underline-offset-2 hover:text-ink"
            >
              {copied ? "copied" : "copy"}
            </button>
          ) : null}
        </p>
        <button
          type="button"
          onClick={() => reset()}
          className="inline-block bg-ink text-white eyebrow-strong px-[30px] py-[17px] hover:bg-accent transition-colors"
        >
          Try again
        </button>
      </div>
    </main>
  );
}
